import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import type { ITimesheetProjectApproval } from '@pulse/shared';
import { Button, Tooltip, Typography } from '@pulse/ui';

interface TimesheetApprovalIndicatorProps {
  approval?: ITimesheetProjectApproval;
  projectId: string;
  /** Omitted (e.g. read-only approver drilldown) hides the per-project Submit button entirely. */
  onSubmitProject?: (projectId: string) => void;
  isSubmitting?: boolean;
}

// Submitted/Approved/Rejected/auto-submitted timesheet-approval status icon + optional
// per-project Submit button — shared between TimesheetGrid's (desktop) project header row and
// TimesheetMobileList's (mobile) project card header, so this logic isn't duplicated across
// both layouts.
export function TimesheetApprovalIndicator({ approval, projectId, onSubmitProject, isSubmitting }: TimesheetApprovalIndicatorProps) {
  return (
    <>
      {approval?.status === 'Submitted' && (
        <Tooltip title={`Submitted${approval.autoSubmitted ? ' automatically' : ''} — awaiting review, locked for editing`}>
          <LockIcon fontSize="inherit" color="disabled" />
        </Tooltip>
      )}
      {approval?.status === 'Approved' && (
        <Tooltip title={`Approved${approval.approvedByName ? ` by ${approval.approvedByName}` : ''} — locked for editing`}>
          <LockIcon fontSize="inherit" color="success" />
        </Tooltip>
      )}
      {approval?.status === 'Rejected' && (
        <Tooltip title={`Rejected${approval.rejectionReason ? `: ${approval.rejectionReason}` : ''}`}>
          <ThumbDownIcon fontSize="inherit" color="error" />
        </Tooltip>
      )}
      {approval?.autoSubmitted && approval.status === 'Submitted' && (
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, fontStyle: 'italic' }}>
          Auto-submitted
        </Typography>
      )}
      {onSubmitProject && approval?.status !== 'Submitted' && approval?.status !== 'Approved' && (
        <Button
          size="small"
          variant="text"
          startIcon={<SendIcon fontSize="small" />}
          disabled={isSubmitting}
          onClick={(e) => { e.stopPropagation(); onSubmitProject(projectId); }}
          sx={{ fontWeight: 400, ml: 'auto' }}
        >
          {approval?.status === 'Rejected' ? 'Re-submit' : 'Submit'}
        </Button>
      )}
    </>
  );
}
